import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Code, ShoppingCart, Globe, PenTool } from "lucide-react";

interface WebProjectProps {
  title: string;
  description: string;
  icon: React.ElementType;
  className?: string;
}

const WebProject = ({ title, description, icon: Icon, className }: WebProjectProps) => {
  return (
    <motion.div
      whileHover={{ scale: 1.03 }}
      className={`p-6 rounded-lg shadow-lg bg-accent-navy/60 border border-gray-800 ${className}`}
    >
      <Icon className="w-10 h-10 text-coral mb-4" />
      <h3 className="text-xl font-semibold mb-2 text-white">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </motion.div>
  );
};

export default function WebDevelopment() {
  const webProjects = [
    {
      icon: ShoppingCart,
      title: "E-commerce Solutions",
      description: "Fully functional online stores with secure payment gateways, inventory management, and customer tracking."
    },
    {
      icon: Code,
      title: "Business Websites",
      description: "Professional and responsive websites that represent your brand's identity and values to potential clients."
    },
    {
      icon: PenTool,
      title: "Portfolio Sites",
      description: "Showcasing your work in an elegant and interactive way to impress potential clients or employers."
    },
    {
      icon: Globe,
      title: "Web Applications",
      description: "Custom-built web applications with rich user interfaces and seamless experiences across devices."
    }
  ];

  return (
    <div className="py-20 px-4 bg-accent-navy min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-10">
          <h1 className="text-4xl font-bold text-white">Web Development</h1>
          <Link 
            to="/" 
            className="text-coral px-4 py-2 rounded hover:coral transition-colors"
          >
            Back to Home
          </Link>
        </div>

        <div className="mb-16">
          <p className="text-gray-100 text-lg mb-4">
            We create modern, responsive websites and web applications that not only look stunning but are also fast, secure, and SEO-friendly.
          </p>
          <p className="text-gray-100 text-lg">
            Our web development team specializes in creating digital experiences that engage users and drive conversions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {webProjects.map((project) => (
            <WebProject
              key={project.title}
              title={project.title}
              description={project.description}
              icon={project.icon}
            />
          ))}
        </div>

        <div className="bg-accent-navy/40 p-8 rounded-lg border border-gray-800">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to start your web project?</h2>
          <p className="text-gray-100 mb-6">
            Contact us to discuss your requirements and get a quote for your custom website or web application.
          </p>
          <Link
            to="/#contact"
            className="inline-block bg-coral text-white px-6 py-3 rounded hover:bg-coral/80 transition-colors"
          >
            Get in Touch
          </Link>
        </div>
      </div>
    </div>
  );
} 